import { useCallback, useMemo, useState } from 'react';
import { AlertTriangle, Loader2, Shield, ShieldOff } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { createMutationHandler } from '@/hooks/mutationHelpers';
import { useOrgWriteGate } from '@/hooks/useRoleCheck';
import { useToast } from '@/hooks/use-toast';
import { useDisplayTimeZone } from '@/hooks/useDisplayTimeZone';
import { cn } from '@/lib/utils';
import { formatUtcDate } from '@/lib/date-utils';
import {
  DURATION_DROPDOWN_LABELS,
  type DurationPreset,
  endOfDayUTC,
  tomorrowUtcStart,
  untilDays,
  untilOneMonth,
} from '@/lib/durationPresets';
import { trackEvent } from '@/lib/analytics';
import {
  isExcludableFindingType,
  NON_EXCLUDABLE_REASON,
} from '@kirin/api-client/contracts/alerts';
import {
  useExcludeAlert,
  type AlertExcludeScope,
} from '@/hooks/api/useAlertExclusions';
import {
  CREATE_EXCLUSION_BUTTON_TOOLTIP,
  SUPPORT_READONLY_TOOLTIP,
} from '@/components/alertActionTooltipCopy';
import { PRESSED_ACTION_BUTTON_CLASS } from '@/components/alertActionStyles';

/**
 * Shared min-width for the Create/Remove Exclusion pair (and the
 * non-excludable placeholder) so toggling does not shift layout. Imported
 * by {@link AlertRemoveExclusionButton}; aligned with the actions column
 * width in the org/user columns factories.
 */
export const EXCLUDE_BUTTON_WIDTH_CLASS = 'min-w-[10.5rem] justify-center';

/**
 * Captured user intent between dropdown/calendar selection and the always-on
 * warning dialog. `excludedUntil` is `null` for the "Forever" preset, an ISO
 * datetime otherwise (already truncated to UTC end-of-day for non-Custom
 * presets and for the Continue handler on the calendar).
 */
interface PendingExclusion {
  preset: DurationPreset;
  excludedUntil: string | null;
}

/**
 * Props for {@link AlertExcludeButton}.
 */
export interface AlertExcludeButtonProps {
  /** Scope of the alert (org-shared or self/account). */
  scope: AlertExcludeScope;
  /** The alert key to exclude. */
  alertKey: string;
  /**
   * Optional className passed to the trigger Button so callers can place the
   * control in tables, toolbars, or detail headers without overriding styles.
   */
  className?: string;
  /** Optional callback invoked after a successful exclusion. */
  onExcluded?: () => void;
  /**
   * Optional analytics dimensions threaded into the `alert_excluded` Mixpanel
   * event. Kept loose so callers (table cells, detail headers) can pass row
   * fields without coupling the button to the full `GroupedAlert` shape.
   * Mirrors `AlertMuteButton`'s `analyticsContext` exactly.
   */
  analyticsContext?: {
    severity?: string;
    type?: string;
  };
  /**
   * Finding `type` of the row (e.g. `secret-leakage`, `untrusted_mcp_server`).
   * When set to a hook-enforced type listed in `NON_EXCLUDABLE_FINDING_TYPES`
   * the button renders disabled with a tooltip explaining why exclusion would
   * have no enforcement effect. Source-of-truth check is the shared
   * `isExcludableFindingType` helper from `@kirin/api-client/contracts/alerts`.
   * Optional so headless callers (no row context) get the existing behavior.
   */
  findingType?: string;
  /**
   * KIR-4700: renders the trigger as an icon-only square button (no visible
   * "Create Exclusion" label) for the condensed alerts-table Actions column.
   * The tooltip still carries the label/reason. Does not affect the confirm
   * dialogs, which keep their text buttons.
   */
  iconOnly?: boolean;
}

/**
 * Full-phrase labels for the four fixed presets, surfaced as the prominent
 * "Exclude …" line directly under the warning dialog title. A separate map
 * from `DURATION_DROPDOWN_LABELS` because the dropdown labels are noun-form
 * ("Forever", "1 day") while this surface needs a complete sentence
 * ("Exclude indefinitely", "Exclude for 1 day"); collapsing them would
 * compromise either the dropdown or the dialog's voice. Lives here (not in
 * `lib/`) because only this dialog consumes the phrase form.
 *
 * Imperative voice ("Exclude …" not "Excluded …") matches the action the
 * Confirm button is about to perform — the user has not yet committed when
 * they read this line, so past-tense was misleading product copy.
 *
 * `Exclude<…, 'custom'>` keeps the type pinned to the four static cases;
 * the calendar-driven `'custom'` branch is handled separately because its
 * label depends on the runtime `excludedUntil` value.
 */
const WARNING_DIALOG_PRESET_LABELS: Readonly<
  Record<Exclude<DurationPreset, 'custom'>, string>
> = {
  forever: 'Exclude indefinitely',
  '1_day': 'Exclude for 1 day',
  '1_week': 'Exclude for 1 week',
  '1_month': 'Exclude for 1 month',
};

/**
 * Renders the prominent "Exclude …" duration line shown under the warning
 * dialog title. Inline (vs. extracted to `lib/`) because these phrases are
 * tightly coupled to the dialog copy and have no other consumer today.
 *
 * The custom-date branch reuses {@link formatUtcDate}: expirations are UTC
 * calendar days, labelled UTC outside the UTC display zone like every other
 * date in the FE.
 */
function describePending(pending: PendingExclusion, timeZone: string): string {
  if (pending.preset !== 'custom') {
    return WARNING_DIALOG_PRESET_LABELS[pending.preset];
  }
  return `Exclude until ${formatUtcDate(pending.excludedUntil, timeZone !== 'UTC', 'further notice')}`;
}

/**
 * The warning dialog's duration line. Its own component so only the open
 * dialog, not every row's Exclude button, re-renders on a display-zone change.
 */
function PendingExclusionLine({
  pending,
}: {
  pending: PendingExclusion;
}): React.ReactElement {
  const timeZone = useDisplayTimeZone();
  return (
    <p className="text-base font-semibold text-foreground">
      {describePending(pending, timeZone)}
    </p>
  );
}

/**
 * Exclude control for a grouped alert (KIR-2363 + KIR-2447).
 *
 * Renders a dropdown with quick-pick durations (Forever, 1 day, 1 week,
 * 1 month) plus a "Custom..." option that opens a calendar dialog. **Every**
 * duration choice — including the calendar path — funnels through a single
 * always-on warning dialog before the mutation is fired. The warning is the
 * UX safety net specified in KIR-2363: exclusion silences enforcement on
 * matching alerts globally, so a confirm step is required regardless of
 * duration.
 *
 * The mutation is performed by {@link useExcludeAlert}, which handles
 * optimistic updates, rollback, and cache invalidation.
 *
 * Gates:
 * - **Role** (org-scope only): hidden for `member` role; visible for
 *   `owner`/`manager`. Account-scope renders for any authenticated user
 *   (members can exclude their own alerts) — mirrors mute's gating exactly.
 *
 * Emits `alert_excluded` after a confirmed mutation success with the same
 * property shape as `alert_muted` (scope, alert_key, severity, type,
 * exclude_duration, excluded_until). The analytics call is wrapped in its
 * own try-block isolated from `onExcluded` so a misbehaving consumer
 * callback cannot suppress the event for a backend-confirmed mutation.
 */
export function AlertExcludeButton({
  scope,
  alertKey,
  className,
  onExcluded,
  analyticsContext,
  findingType,
  iconOnly,
}: AlertExcludeButtonProps): React.ReactElement | null {
  // Source of truth lives in `@kirin/api-client/contracts/alerts`. The
  // backend rejects the same set with `ALERT_NOT_EXCLUDABLE` 400, so a
  // tampered or out-of-date client cannot bypass this gate by faking the
  // mutation locally. Defensive `?? undefined` keeps the helper's nullish
  // contract honest when consumers pass empty strings.
  const excludable = isExcludableFindingType(findingType ?? undefined);

  const writeGate = useOrgWriteGate(
    scope.kind === 'organization' ? scope.organizationId : undefined
  );

  // Calendar state (Custom path only). This dialog never fires the mutation
  // directly — its Continue handler transitions to the warning dialog by
  // setting `pending`, which is what guarantees Custom is also gated.
  const [customOpen, setCustomOpen] = useState(false);
  const [customDate, setCustomDate] = useState<Date | undefined>(undefined);

  // The single source of truth for the warning dialog: when non-null, the
  // dialog is open and the user has chosen a duration but not yet confirmed.
  // All five duration paths (Forever, 1 day, 1 week, 1 month, Custom) MUST
  // set this before the mutation fires.
  const [pending, setPending] = useState<PendingExclusion | null>(null);

  // Centralizes "calendar dismissed" cleanup so every dismiss path (Cancel
  // button, Esc, backdrop click, programmatic close after Continue) clears
  // the selected date. Without this, a date picked then dismissed via Esc
  // would persist and prefill the calendar on the next open.
  const handleCustomOpenChange = useCallback((open: boolean): void => {
    setCustomOpen(open);
    if (!open) setCustomDate(undefined);
  }, []);

  // Memoize the calendar's lower bound so we don't allocate a fresh `Date`
  // + matcher object on every render. react-day-picker compares the matcher
  // by reference, so reusing the same instance avoids superfluous work.
  const minDate = useMemo(() => tomorrowUtcStart(), []);

  const excludeMutation = useExcludeAlert(scope);
  const { toast } = useToast();

  // Wrap mutateAsync with the shared toast handler so success/failure produce
  // a consistent UX across every consumer (table row, detail header, etc.).
  // The hook still owns optimistic updates and rollback; this layer only
  // adds user-facing messaging.
  const handleExclude = createMutationHandler(excludeMutation, toast, {
    successMessage: 'Alert excluded',
    errorMessage: 'Failed to exclude alert',
  });

  if (scope.kind === 'organization') {
    if (writeGate.isLoading) return null;
    if (writeGate.hidden) return null;
  }
  const writeDisabled = scope.kind === 'organization' && writeGate.disabled;

  const handleWarningOpenChange = (open: boolean): void => {
    if (!open) setPending(null);
  };

  const handleCustomContinue = (): void => {
    if (!customDate) return;
    const iso = endOfDayUTC(customDate);
    handleCustomOpenChange(false);
    setPending({ preset: 'custom', excludedUntil: iso });
  };

  const handleConfirmPending = async (): Promise<void> => {
    if (!pending) return;
    try {
      await handleExclude({ alertKey, excludedUntil: pending.excludedUntil });
      // All success-only side effects below are deliberately ISOLATED from
      // each other: a throw in one must never block the others or — most
      // importantly — the `setPending(null)` dismissal at the end. The
      // mutation has been confirmed by the backend; from the user's
      // perspective the exclusion is committed and the warning dialog must
      // close. A thrown trackEvent (Mixpanel SDK errors are a real risk —
      // the library's internals are not fully wrapped) routed to the outer
      // catch would otherwise leave the dialog stuck open as if the
      // mutation itself had failed. Order matches `AlertMuteButton`:
      // callback first (user-observable), then analytics (background).
      try {
        onExcluded?.();
      } catch {
        // Caller-supplied callbacks are out of our control. Swallowing here
        // ensures a misbehaving consumer can't suppress the analytics event
        // for a mutation the backend has already accepted.
      }
      try {
        trackEvent('alert_excluded', {
          scope: scope.kind,
          alert_key: alertKey,
          severity: analyticsContext?.severity,
          type: analyticsContext?.type,
          exclude_duration: pending.preset,
          excluded_until: pending.excludedUntil,
        });
      } catch {
        // Telemetry is best-effort; never let it propagate to the outer
        // mutation-failure handler. See the block comment above.
      }
      setPending(null);
    } catch {
      // createMutationHandler already toasted; leaving `pending` set keeps
      // the warning dialog open so the user can retry without re-picking
      // the duration. The Confirm button re-enables once `isPending`
      // flips back to false, mirroring mute's retry semantics.
    }
  };

  const isPending = excludeMutation.isPending;

  // Render a non-interactive disabled button + tooltip when the finding type
  // is enforced client-side by hooks (currently just `secret-leakage`). The
  // dropdown / dialogs are intentionally NOT mounted in this branch so the
  // mutation surface is unreachable — both for clicks and for keyboard.
  // The button is wrapped in a focusable <span> so Radix Tooltip still
  // receives pointer/focus events; native `disabled` attributes suppress
  // them and would hide the explanatory tooltip on hover. `pointer-events-none`
  // on the inner Button is the structural guard that makes pointer activation
  // unreachable; `opacity-50` is the visual affordance.
  if (!excludable) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} className="inline-flex">
            <Button
              type="button"
              variant="outline"
              size={iconOnly ? 'icon-sm' : 'sm'}
              className={cn(
                !iconOnly && EXCLUDE_BUTTON_WIDTH_CLASS,
                'pointer-events-none opacity-50',
                className
              )}
              aria-disabled="true"
              aria-label="Exclude alert (unavailable)"
              data-testid="alert-exclude-button-disabled"
              onClick={event => event.preventDefault()}
            >
              <Shield className="size-4" />
              {!iconOnly && <span>Create Exclusion</span>}
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>{NON_EXCLUDABLE_REASON}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <>
      <DropdownMenu>
        {/* Both Radix triggers `asChild` collapse to a single <button>; the
            tooltip dismisses on dropdown open. The non-excludable branch
            above keeps its own NON_EXCLUDABLE_REASON tooltip. */}
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size={iconOnly ? 'icon-sm' : 'sm'}
                className={cn(
                  !iconOnly && EXCLUDE_BUTTON_WIDTH_CLASS,
                  PRESSED_ACTION_BUTTON_CLASS,
                  className
                )}
                disabled={isPending || writeDisabled}
                aria-label="Exclude alert"
              >
                {isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Shield className="size-4" />
                )}
                {!iconOnly && <span>Create Exclusion</span>}
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>
            {writeDisabled
              ? SUPPORT_READONLY_TOOLTIP
              : CREATE_EXCLUSION_BUTTON_TOOLTIP}
          </TooltipContent>
        </Tooltip>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onSelect={() =>
              setPending({ preset: 'forever', excludedUntil: null })
            }
          >
            {DURATION_DROPDOWN_LABELS.forever}
          </DropdownMenuItem>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>For</DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuItem
                onSelect={() =>
                  setPending({ preset: '1_day', excludedUntil: untilDays(1) })
                }
              >
                {DURATION_DROPDOWN_LABELS['1_day']}
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() =>
                  setPending({ preset: '1_week', excludedUntil: untilDays(7) })
                }
              >
                {DURATION_DROPDOWN_LABELS['1_week']}
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() =>
                  setPending({
                    preset: '1_month',
                    excludedUntil: untilOneMonth(),
                  })
                }
              >
                {DURATION_DROPDOWN_LABELS['1_month']}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setCustomOpen(true)}>
                {DURATION_DROPDOWN_LABELS.custom}
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={customOpen} onOpenChange={handleCustomOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Exclude alert until…</DialogTitle>
            <DialogDescription>
              Pick the day this alert should be excluded through. The exclusion
              expires at end-of-day UTC.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center py-2">
            <Calendar
              mode="single"
              selected={customDate}
              onSelect={setCustomDate}
              disabled={{ before: minDate }}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleCustomOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleCustomContinue}
              disabled={!customDate || isPending}
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={pending !== null} onOpenChange={handleWarningOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="size-5" aria-hidden="true" />
              Create Exclusion - Warning
            </DialogTitle>
            {pending ? <PendingExclusionLine pending={pending} /> : null}
            <DialogDescription className="font-semibold text-foreground">
              Creating an exclusion will disable prevention for activity
              matching this alert.
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Activity matching this pattern will no longer trigger enforcement
            actions and threats may proceed unblocked and undetected. Create
            exclusions only for alerts you have fully investigated and confirmed
            as safe.
          </p>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setPending(null)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => void handleConfirmPending()}
              disabled={isPending}
            >
              {isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <ShieldOff className="size-4" aria-hidden="true" />
              )}
              <span>Create Exclusion</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

AlertExcludeButton.displayName = 'AlertExcludeButton';
